"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect } from "react";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useLayout } from "./layout/LayoutContext";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import ParticipationDrop from "@/components/waves/drops/participation/ParticipationDrop";
import { DropLocation } from "@/components/waves/drops/drop.types";
import { WaveLeaderboardLoading } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoading";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
const CURATION_FILTER_PARAM = "curation_id";
interface MyStreamWaveSubmissionsProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveSubmissions: React.FC<MyStreamWaveSubmissionsProps> = t0 => {
  const $ = _c(45);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchParamsString = t1;
  const {
    leaderboardViewStyle
  } = useLayout();
  let t2;
  if ($[2] !== wave.id) {
    t2 = {
      waveId: wave.id,
      sort: WaveDropsLeaderboardSort.RANK,
      curatedByGroupId: undefined
    };
    $[2] = wave.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useWaveDropsLeaderboard(t2);
  const handleReply = _temp;
  let t3;
  if ($[4] !== refetch) {
    t3 = () => {
      refetch();
    };
    $[4] = refetch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleRetry = t3;
  let t4;
  let t5;
  if ($[6] !== pathname || $[7] !== router || $[8] !== searchParamsString) {
    t4 = () => {
      const params = new URLSearchParams(searchParamsString || "");
      if (!params.has(CURATION_FILTER_PARAM)) {
        return;
      }
      params.delete(CURATION_FILTER_PARAM);
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    t5 = [pathname, router, searchParamsString];
    $[6] = pathname;
    $[7] = router;
    $[8] = searchParamsString;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== pathname || $[12] !== router || $[13] !== searchParamsString) {
    t6 = dropId => {
      const params_0 = new URLSearchParams(searchParamsString || "");
      params_0.delete(CURATION_FILTER_PARAM);
      params_0.set("drop", dropId);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[11] = pathname;
    $[12] = router;
    $[13] = searchParamsString;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const openDropById = t6;
  let t7;
  if ($[15] !== openDropById) {
    t7 = drop => {
      openDropById(drop.id);
    };
    $[15] = openDropById;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleQuoteClick = t7;
  let t8;
  if ($[17] !== fetchNextPage || $[18] !== hasNextPage || $[19] !== isFetching || $[20] !== isFetchingNextPage) {
    t8 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetching || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[17] = fetchNextPage;
    $[18] = hasNextPage;
    $[19] = isFetching;
    $[20] = isFetchingNextPage;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleIntersection = t8;
  const intersectionElementRef = useIntersectionObserver(handleIntersection);
  let content;
  if (isError && drops.length === 0) {
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <p className="tw-mb-0 tw-text-sm tw-text-iron-300">Unable to load submissions.</p>;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== handleRetry) {
      t10 = <div className="tw-mt-10 tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">{t9}<button type="button" onClick={handleRetry} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-500 tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-iron-100 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-bg-iron-800">Try again</button></div>;
      $[23] = handleRetry;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    content = t10;
  } else {
    if (isFetching && drops.length === 0) {
      let t9;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <WaveLeaderboardLoading />;
        $[25] = t9;
      } else {
        t9 = $[25];
      }
      content = t9;
    } else {
      if (drops.length === 0) {
        let t9;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <div className="tw-mt-10"><p className="tw-text-center tw-text-sm tw-text-iron-500">No submissions to show.</p></div>;
          $[26] = t9;
        } else {
          t9 = $[26];
        }
        content = t9;
      } else {
        let t9;
        if ($[27] !== drops || $[28] !== handleQuoteClick || $[29] !== onDropClick) {
          let t10;
          if ($[31] !== handleQuoteClick || $[32] !== onDropClick) {
            t10 = drop_0 => <ParticipationDrop key={drop_0.id} drop={drop_0} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} onReply={handleReply} onQuoteClick={handleQuoteClick} onDropContentClick={onDropClick} />;
            $[31] = handleQuoteClick;
            $[32] = onDropClick;
            $[33] = t10;
          } else {
            t10 = $[33];
          }
          t9 = drops.map(t10);
          $[27] = drops;
          $[28] = handleQuoteClick;
          $[29] = onDropClick;
          $[30] = t9;
        } else {
          t9 = $[30];
        }
        let t10;
        if ($[34] !== isFetchingNextPage) {
          t10 = isFetchingNextPage && <WaveLeaderboardLoadingBar />;
          $[34] = isFetchingNextPage;
          $[35] = t10;
        } else {
          t10 = $[35];
        }
        let t11;
        if ($[36] !== intersectionElementRef) {
          t11 = <div ref={intersectionElementRef} />;
          $[36] = intersectionElementRef;
          $[37] = t11;
        } else {
          t11 = $[37];
        }
        let t12;
        if ($[38] !== t10 || $[39] !== t11 || $[40] !== t9) {
          t12 = <div className="tw-mt-4 tw-space-y-2">{t9}{t10}{t11}</div>;
          $[38] = t10;
          $[39] = t11;
          $[40] = t9;
          $[41] = t12;
        } else {
          t12 = $[41];
        }
        content = t12;
      }
    }
  }
  let t9;
  if ($[42] !== content || $[43] !== leaderboardViewStyle) {
    t9 = <div className="tw-space-y-4 tw-overflow-y-auto tw-px-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-px-4 lg:tw-space-y-6" style={leaderboardViewStyle}>{content}</div>;
    $[42] = content;
    $[43] = leaderboardViewStyle;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  return t9;
};
export default MyStreamWaveSubmissions;
function _temp() {}